import {create} from "zustand";

/**
 * @interface AccionNotificacion
 * @description Botón opcional de una notificación: al usarlo se ejecuta `onClick` y la notificación
 * se cierra.
 */
export interface AccionNotificacion {
  /**
   * @property {string} etiqueta - Texto del botón (por ejemplo, "Ver").
   */
  etiqueta: string;
  /**
   * @property {() => void} onClick - Lo que hace el botón. Después la notificación se cierra.
   */
  onClick: () => void;
}

/**
 * @interface Notificacion
 * @description Define la estructura de una notificación individual mostrada en la UI.
 */
export interface Notificacion {
  /**
   * @property {number} id - Identificador único de la notificación, generado con timestamp + aleatorio.
   */
  id: number;
  /**
   * @property {string} mensaje - El texto a mostrar dentro de la notificación.
   */
  mensaje: string;
  /**
   * @property {"success" | "error" | "info" | "warning"} gravedad - Nivel de severidad de la
   * notificación; controla el color y el icono que se muestra en la UI.
   */
  gravedad: "success" | "error" | "info" | "warning";
  /**
   * @property {AccionNotificacion} [accion] - Botón opcional bajo el mensaje. Una notificación sin
   * acción no lleva esta propiedad.
   */
  accion?: AccionNotificacion;
}

/**
 * @interface NotificacionState
 * @description Define el estado y las acciones para el store de notificaciones.
 * @property {Notificacion[]} notificaciones - Las notificaciones en pantalla (se muestra la última).
 * @property {(mensaje: string, gravedad?: "success" | "error" | "info" | "warning", accion?: AccionNotificacion) => void} showNotification - Acción para mostrar una nueva notificación, con un botón opcional.
 * @property {(id: number) => void} closeNotification - Acción para cerrar una notificación (también olvida su pausa).
 * @property {Record<number, number>} pausasCierre - Cuántas copias del aviso pausan su cierre automático, por id.
 * @property {(id: number, pausar: boolean) => void} pausarCierre - Pide o suelta la pausa del cierre automático de un aviso.
 */
interface NotificacionState {
	notificaciones: Notificacion[];
	/**
	 * Por id de notificación, cuántas copias montadas del aviso piden pausar su cierre automático
	 * (el puntero o el foco está encima). Vive en el store porque el aviso puede montarse más de una
	 * vez y todas las copias deben respetar la misma pausa. Sin entrada = sin pausa.
	 */
	pausasCierre: Record<number, number>;
	showNotification: (
		mensaje: string,
		gravedad?: Notificacion["gravedad"],
		accion?: AccionNotificacion
	) => void;
	closeNotification: (id: number) => void;
	/** Una copia del aviso pide (`true`) o suelta (`false`) la pausa de su cierre automático. */
	pausarCierre: (id: number, pausar: boolean) => void;
}

/**
 * Un store de Zustand para manejar notificaciones globales en la aplicación.
 * Permite agregar y eliminar notificaciones, cada una con un mensaje, un nivel de severidad y, si se
 * pide, un botón de acción. Las notificaciones se almacenan en un array, permitiendo múltiples
 * notificaciones simultáneas.
 *
 * @store
 * @returns {NotificacionState} El estado y las acciones del store de notificaciones.
 */
export const useNotificacionStore = create<NotificacionState>((set) => ({
	notificaciones: [],

	showNotification: (
		mensaje: string,
		gravedad: Notificacion["gravedad"] = "info",
		accion?: AccionNotificacion
	) =>
		set((state) => {
			// Generar un ID único usando timestamp + contador para evitar colisiones
			// Usamos el timestamp multiplicado por 1000 y sumamos un número aleatorio entre 0-999
			const id = Date.now() * 1000 + Math.floor(Math.random() * 1000);
			// Sin acción, la notificación conserva exactamente la forma de siempre.
			const notificacion: Notificacion = accion
				? {id, mensaje, gravedad, accion}
				: {id, mensaje, gravedad};
			return {
				notificaciones: [...state.notificaciones, notificacion],
			};
		}),

	closeNotification: (id) =>
		set((state) => ({
			notificaciones: state.notificaciones.filter(
				(notificacion) => notificacion.id !== id
			),
			pausasCierre: sinPausa(state.pausasCierre, id),
		})),

	pausasCierre: {},

	pausarCierre: (id, pausar) =>
		set((state) => {
			const cuantas = Math.max(0, (state.pausasCierre[id] ?? 0) + (pausar ? 1 : -1));
			return {
				pausasCierre: cuantas > 0 ? {...state.pausasCierre, [id]: cuantas} : sinPausa(state.pausasCierre, id),
			};
		}),
}));

/** Las pausas sin la del aviso `id`. */
function sinPausa(pausas: Record<number, number>, id: number): Record<number, number> {
	if (!(id in pausas)) {
		return pausas;
	}
	const resto = {...pausas};
	delete resto[id];
	return resto;
}
